import { fetchStudioTargets, upsertStudioTarget, deleteStudioTarget, fetchStudioBudget, saveStudioBudget } from '../services/db/studio.pocketbase.js';
import {
  fetchUserLabelCatalog,
  createUserLabel,
  renameUserLabel,
  deleteUserLabel,
  updateUserLabelColor,
  normalizeLabelName,
  replaceLabelInList,
  removeLabelFromList,
} from '../services/db/studioLabels.pocketbase.js';
import { DEFAULT_STUDIO_LABEL_COLOR, applyLabelChipColor, normalizeLabelColor } from './studioLabelColors.js';
import {
  fetchStudioDrafts,
  upsertStudioDraft,
  deleteStudioDraft,
} from '../services/db/studioDrafts.pocketbase.js';
import {
  loadStudioDraftsFromStorage,
  saveStudioDraftsToStorage,
  mergeStudioDraft,
  isStudioDraftEmpty,
  normalizeStudioDraft,
} from '../services/studioDrafts.js';
import { reconcileStudioTargets } from './studioReconcile.js';
import { UNASSIGNED_STUDIO_SLOT_KEY } from './studioCalc.js';

export const STUDIO_COMPARE_SLOT_COUNT = 5;

function refreshStudioAfterLabelCatalogChange(ctx, { render = true } = {}) {
  if (render && !document.getElementById('studio-labels-modal')) {
    ctx.renderStudioView();
    ctx.syncStudioButtonsInTable();
  } else {
    ctx.refreshStudioLabelCatalogUi?.();
  }
}

export function createEmptyCompareSlots() {
  return Array(STUDIO_COMPARE_SLOT_COUNT).fill(null);
}
import { BANDS, normalizeBand } from './studioLabels.js';
import { renderStudioListActions, renderLiveAstaActions } from '../ui/action-buttons.js';
import { filtersManager } from '../features/filters/index.js';
import { renderPriceBandPickerHtml } from './studioPriceBands.js';
import { escapeHtml } from '../utils/escapeHtml.js';
import { parsePlayerKey } from '../utils/playerKey.js';

/** Unisce due bozze: prezzo più alto, note più ricche, etichette in unione. */
function mergeDraftPreferRicher(local, server) {
  const a = normalizeStudioDraft(local);
  const b = normalizeStudioDraft(server);
  const maxPrice = Math.max(a.maxPrice || 0, b.maxPrice || 0);
  let notes = a.notes;
  if (!a.notes) notes = b.notes;
  else if (!b.notes) notes = a.notes;
  else notes = a.notes.length >= b.notes.length ? a.notes : b.notes;
  const labels = [...new Set([...(a.labels || []), ...(b.labels || [])])];
  return normalizeStudioDraft({ maxPrice, notes, labels });
}

function draftFingerprint(draft) {
  const d = normalizeStudioDraft(draft);
  return JSON.stringify({
    maxPrice: d.maxPrice,
    notes: d.notes,
    labels: [...(d.labels || [])].sort(),
  });
}

export const studioMethods = {
  loadStudioDrafts() {
    if (!this.state.studioDrafts) this.state.studioDrafts = new Map();
    const accessKey = this.state.userSession?.getAccessCode?.();
    if (!accessKey) {
      this.state.studioDrafts = new Map();
      this._studioDraftsPersistedKeys = new Set();
      this._studioDraftsPersistedSnap = new Map();
      return;
    }
    this.state.studioDrafts = loadStudioDraftsFromStorage(accessKey);
    // Evita bozze residue su giocatori già nello Studio
    for (const key of this.state.studioTargets.keys()) {
      if (this.state.studioDrafts.has(key)) this.state.studioDrafts.delete(key);
    }
    saveStudioDraftsToStorage(accessKey, this.state.studioDrafts);
    if (!(this._studioDraftsPersistedKeys instanceof Set)) {
      this._studioDraftsPersistedKeys = new Set(this.state.studioDrafts.keys());
    }
    if (!(this._studioDraftsPersistedSnap instanceof Map)) {
      this._studioDraftsPersistedSnap = new Map(
        [...this.state.studioDrafts.entries()].map(([k, d]) => [k, draftFingerprint(d)]),
      );
    }
  },

  /**
   * Scarica bozze da PocketBase, le unisce al cache localStorage e ripulisce
   * chiavi già presenti come target Studio.
   */
  async syncStudioDraftsFromServer() {
    const accessKey = this.state.userSession?.getAccessCode?.();
    if (!accessKey) return;
    if (!(this.state.studioDrafts instanceof Map)) {
      this.state.studioDrafts = loadStudioDraftsFromStorage(accessKey);
    }
    try {
      const remote = await fetchStudioDrafts(accessKey);
      const targets = this.state.studioTargets instanceof Map ? this.state.studioTargets : new Map();
      const toUpload = [];
      for (const [key, serverDraft] of Object.entries(remote)) {
        if (targets.has(key)) {
          void deleteStudioDraft(accessKey, key).catch((err) => {
            console.warn('[Studio] Cleanup draft su target fallito:', key, err);
          });
          this.state.studioDrafts.delete(key);
          continue;
        }
        const local = this.state.studioDrafts.get(key);
        const serverNorm = normalizeStudioDraft(serverDraft);
        if (!local) {
          this.state.studioDrafts.set(key, serverNorm);
        } else {
          const merged = mergeDraftPreferRicher(local, serverDraft);
          this.state.studioDrafts.set(key, merged);
          if (draftFingerprint(merged) !== draftFingerprint(serverNorm)) {
            toUpload.push(key);
          }
        }
      }
      for (const key of targets.keys()) {
        if (this.state.studioDrafts.has(key)) this.state.studioDrafts.delete(key);
      }
      for (const [key] of this.state.studioDrafts.entries()) {
        if (!(key in remote) && !targets.has(key)) toUpload.push(key);
      }
      saveStudioDraftsToStorage(accessKey, this.state.studioDrafts);
      this._studioDraftsPersistedKeys = new Set(this.state.studioDrafts.keys());
      this._studioDraftsPersistedSnap = new Map(
        [...this.state.studioDrafts.entries()].map(([k, d]) => [k, draftFingerprint(d)]),
      );
      // Solo bozze locali o merge più ricco del server
      await Promise.all(
        [...new Set(toUpload)].map((key) => {
          const draft = this.state.studioDrafts.get(key);
          if (!draft) return Promise.resolve();
          return upsertStudioDraft({
            accessKey,
            playerKey: key,
            maxPrice: draft.maxPrice,
            notes: draft.notes,
            labels: draft.labels,
          }).catch((err) => {
            console.warn('[Studio] Upload draft locale fallito:', key, err);
          });
        }),
      );
    } catch (err) {
      console.warn('[Studio] Sync drafts da server fallito:', err);
    }
  },

  _persistStudioDrafts() {
    const accessKey = this.state.userSession?.getAccessCode?.();
    if (!accessKey) return;
    if (!(this.state.studioDrafts instanceof Map)) this.state.studioDrafts = new Map();
    saveStudioDraftsToStorage(accessKey, this.state.studioDrafts);

    const currentKeys = new Set(this.state.studioDrafts.keys());
    const prevKeys = this._studioDraftsPersistedKeys instanceof Set
      ? this._studioDraftsPersistedKeys
      : new Set();
    const prevSnap = this._studioDraftsPersistedSnap instanceof Map
      ? this._studioDraftsPersistedSnap
      : new Map();

    const toUpsert = [];
    const nextSnap = new Map();
    for (const key of currentKeys) {
      const d = normalizeStudioDraft(this.state.studioDrafts.get(key));
      const fp = draftFingerprint(d);
      nextSnap.set(key, fp);
      if (prevSnap.get(key) !== fp) toUpsert.push([key, d]);
    }
    const toDelete = [...prevKeys].filter((key) => !currentKeys.has(key));

    this._studioDraftsPersistedKeys = currentKeys;
    this._studioDraftsPersistedSnap = nextSnap;

    if (toUpsert.length === 0 && toDelete.length === 0) return;

    void (async () => {
      try {
        await Promise.all([
          ...toUpsert.map(([key, d]) =>
            upsertStudioDraft({
              accessKey,
              playerKey: key,
              maxPrice: d.maxPrice,
              notes: d.notes,
              labels: d.labels,
            }),
          ),
          ...toDelete.map((key) => deleteStudioDraft(accessKey, key)),
        ]);
      } catch (err) {
        console.warn('[Studio] Persist drafts su PocketBase fallito:', err);
      }
    })();
  },

  getStudioDraft(playerKey) {
    if (!(this.state.studioDrafts instanceof Map)) return null;
    const d = this.state.studioDrafts.get(playerKey);
    return d ? normalizeStudioDraft(d) : null;
  },

  /** Seed UI lista: target Studio oppure bozza Svincolati (senza slot). */
  getStudioListSeed(playerKey) {
    if (this.state.studioTargets.has(playerKey)) {
      return this.state.studioTargets.get(playerKey);
    }
    const draft = this.getStudioDraft(playerKey);
    if (draft) return { ...draft, band: null };
    return { maxPrice: 0, notes: '', labels: [], band: null };
  },

  setStudioDraftFields(playerKey, fields = {}) {
    if (!playerKey || this.state.studioTargets.has(playerKey)) return null;
    if (!(this.state.studioDrafts instanceof Map)) this.state.studioDrafts = new Map();
    const prevDraft = this.getStudioDraft(playerKey);
    const hadDraft = Boolean(prevDraft && !isStudioDraftEmpty(prevDraft));
    const merged = mergeStudioDraft(this.state.studioDrafts.get(playerKey), fields);
    if (isStudioDraftEmpty(merged)) this.state.studioDrafts.delete(playerKey);
    else this.state.studioDrafts.set(playerKey, merged);
    const hasDraft = !isStudioDraftEmpty(merged);
    const draftMembershipChanged = hadDraft !== hasDraft;

    this._persistStudioDrafts();
    this._syncStudioCardQuickFields?.(playerKey, { ...merged, band: null });

    if (fields.labels !== undefined) {
      this._syncStudioRowTagsUi?.(playerKey, merged.labels || []);
      const studioScreen = document.getElementById('screen-studio');
      if (studioScreen && !studioScreen.hidden) {
        this.renderStudioToolbar?.();
      }
    }

    if (draftMembershipChanged) {
      // Bozza entrata/uscita da Studio: stacca subito i pannelli azione (es. tag aperti)
      // prima che la riga sparisca dal DOM.
      this._closeOtherStudioRowPopovers?.(null);
      this._unpinAllStudioPopoverPanels?.();
      this.syncListaStudioFilterContext?.();
      const studioScreen = document.getElementById('screen-studio');
      if (studioScreen && !studioScreen.hidden) {
        this.renderStudioView?.();
      } else {
        this.syncStudioButtonsInTable?.();
      }
    }

    return merged;
  },

  clearStudioDraft(playerKey) {
    if (!(this.state.studioDrafts instanceof Map)) return;
    if (!this.state.studioDrafts.has(playerKey)) return;
    this.state.studioDrafts.delete(playerKey);
    this._persistStudioDrafts();
    this.syncListaStudioFilterContext?.();
  },

  async promoteStudioDraft(playerKey, band, extraFields = {}) {
    const draft = this.getStudioDraft(playerKey) || normalizeStudioDraft({});
    const payload = {
      maxPrice: extraFields.maxPrice !== undefined ? extraFields.maxPrice : draft.maxPrice,
      notes: extraFields.notes !== undefined ? extraFields.notes : draft.notes,
      labels: extraFields.labels !== undefined ? extraFields.labels : draft.labels,
      band,
    };
    await this.addToStudio(playerKey, payload);
    this.clearStudioDraft(playerKey);
  },

  async loadStudioTargets() {
    if (!this.state.userSession) return;
    try {
      const accessKey = this.state.userSession.getAccessCode();
      const rows = await fetchStudioTargets(accessKey);
      this.state.studioTargets = new Map(
        rows.map((r, index) => [
          r.player_key,
          {
            maxPrice: r.max_price,
            notes: r.notes,
            sold: r.sold,
            band: normalizeBand(r.band),
            labels: Array.isArray(r.labels) ? r.labels : [],
            backups: Array.isArray(r.backups) ? r.backups : [],
            missing: !!r.missing,
            sortOrder: Number.isFinite(Number(r.sort_order)) ? Number(r.sort_order) : index,
          },
        ]),
      );
      this.updateStudioTabCount();
      // Bozze: cache locale poi merge server (tag/prezzo/note senza slot)
      this.loadStudioDrafts();
      await this.syncStudioDraftsFromServer();
    } catch (err) {
      console.error('[Studio] Errore caricamento targets:', err);
    }
  },

  async loadStudioUserLabels() {
    if (!this.state.userSession) return;
    try {
      const accessKey = this.state.userSession.getAccessCode();
      let catalog = await fetchUserLabelCatalog(accessKey);
      const catalogLower = new Set(catalog.map((r) => r.name.toLowerCase()));
      const missingFromTargets = new Set();
      for (const [, t] of this.state.studioTargets.entries()) {
        for (const l of t.labels || []) {
          const name = normalizeLabelName(l);
          if (!name) continue;
          if (!catalogLower.has(name.toLowerCase())) missingFromTargets.add(name);
        }
      }
      for (const name of missingFromTargets) {
        try {
          const rec = await createUserLabel(accessKey, name);
          catalogLower.add(name.toLowerCase());
          catalog.push({
            name: normalizeLabelName(rec.name || name),
            color: rec.color || DEFAULT_STUDIO_LABEL_COLOR,
          });
        } catch (err) {
          console.warn('[Studio] Seed etichetta fallito:', name, err);
        }
      }
      catalog = catalog
        .map((r) => ({
          name: normalizeLabelName(r.name),
          color: r.color || DEFAULT_STUDIO_LABEL_COLOR,
        }))
        .filter((r) => r.name)
        .sort((a, b) => a.name.localeCompare(b.name, 'it'));
      this.state.studioUserLabels = catalog.map((r) => r.name);
      this.state.studioUserLabelColors = Object.fromEntries(catalog.map((r) => [r.name, r.color]));
    } catch (err) {
      console.error('[Studio] Errore caricamento etichette utente:', err);
      this.state.studioUserLabels = [];
      this.state.studioUserLabelColors = {};
    }
  },

  getStudioLabelColor(name) {
    const n = normalizeLabelName(name);
    const colors = this.state.studioUserLabelColors || {};
    if (colors[n]) return colors[n];
    const key = Object.keys(colors).find((k) => k.toLowerCase() === n.toLowerCase());
    return key ? colors[key] : DEFAULT_STUDIO_LABEL_COLOR;
  },

  /** Catalogo chip: etichette utente (+ colori salvati + eventuali già sul target). */
  getStudioLabelOptions(extraLabels = []) {
    const labels = new Set(this.state.studioUserLabels || []);
    for (const k of Object.keys(this.state.studioUserLabelColors || {})) {
      const name = normalizeLabelName(k);
      if (name) labels.add(name);
    }
    for (const l of extraLabels || []) {
      const name = normalizeLabelName(l);
      if (name) labels.add(name);
    }
    return {
      custom: Array.from(labels).sort((a, b) => a.localeCompare(b, 'it')),
    };
  },

  _ensureStudioUserLabelInCatalog(name) {
    const canonical = normalizeLabelName(name);
    if (!canonical) return;
    const list = this.state.studioUserLabels || [];
    if (!list.some((l) => normalizeLabelName(l).toLowerCase() === canonical.toLowerCase())) {
      this.state.studioUserLabels = [...list, canonical].sort((a, b) => a.localeCompare(b, 'it'));
    }
  },

  /** Garantisce che il tag esista in catalogo/PB senza toccare il colore già salvato. */
  async ensureStudioUserLabelInCatalog(name) {
    const canonical = normalizeLabelName(name);
    if (!canonical || !this.state.userSession) return canonical || null;
    this._ensureStudioUserLabelInCatalog(canonical);
    try {
      const accessKey = this.state.userSession.getAccessCode();
      const rec = await createUserLabel(accessKey, canonical);
      const savedName = normalizeLabelName(rec.name || canonical);
      this._ensureStudioUserLabelInCatalog(savedName);
      if (rec.color) {
        this.state.studioUserLabelColors = {
          ...(this.state.studioUserLabelColors || {}),
          [savedName]: normalizeLabelColor(rec.color),
        };
      }
      return savedName;
    } catch (err) {
      console.warn('[Studio] ensure label in catalog:', err);
      return canonical;
    }
  },

  countTargetsWithLabel(name) {
    const n = normalizeLabelName(name);
    let count = 0;
    for (const [, t] of this.state.studioTargets.entries()) {
      if ((t.labels || []).includes(n)) count += 1;
    }
    return count;
  },

  async createStudioUserLabel(name, color) {
    if (!this.state.userSession) return null;
    const accessKey = this.state.userSession.getAccessCode();
    const rec = await createUserLabel(accessKey, name, color);
    const canonical = normalizeLabelName(rec.name || name);
    const labelColor = rec.color || color || DEFAULT_STUDIO_LABEL_COLOR;
    const list = this.state.studioUserLabels || [];
    if (!list.some((l) => normalizeLabelName(l).toLowerCase() === canonical.toLowerCase())) {
      this.state.studioUserLabels = [...list, canonical].sort((a, b) => a.localeCompare(b, 'it'));
    }
    this.state.studioUserLabelColors = {
      ...(this.state.studioUserLabelColors || {}),
      [canonical]: normalizeLabelColor(labelColor),
    };
    this.refreshStudioLabelCatalogUi?.();
    return canonical;
  },

  async updateStudioUserLabelColor(name, color, { render = true } = {}) {
    if (!this.state.userSession) {
      console.warn('[Studio] update label color: sessione assente');
      return;
    }
    const normalized = normalizeLabelName(name);
    if (!normalized) return;
    const labelColor = normalizeLabelColor(color);
    const accessKey = this.state.userSession.getAccessCode();

    try {
      const res = await updateUserLabelColor(accessKey, normalized, labelColor);
      const savedName = normalizeLabelName(res.name || normalized);
      const savedColor = normalizeLabelColor(res.color || labelColor);
      this._ensureStudioUserLabelInCatalog(savedName);
      const colors = { ...(this.state.studioUserLabelColors || {}) };
      for (const key of Object.keys(colors)) {
        if (key.toLowerCase() === savedName.toLowerCase() && key !== savedName) {
          delete colors[key];
        }
      }
      colors[savedName] = savedColor;
      this.state.studioUserLabelColors = colors;
      refreshStudioAfterLabelCatalogChange(this, { render });
      return res;
    } catch (err) {
      console.warn('[Studio] update label color:', err);
      throw err;
    }
  },

  async renameStudioUserLabel(oldName, newName) {
    if (!this.state.userSession) return;
    const accessKey = this.state.userSession.getAccessCode();
    const res = await renameUserLabel(accessKey, oldName, newName);
    const from = res.from || normalizeLabelName(oldName);
    const to = res.to || normalizeLabelName(newName);
    this.state.studioUserLabels = (this.state.studioUserLabels || [])
      .map((l) => (l === from ? to : l))
      .filter((l, i, arr) => arr.findIndex((x) => x.toLowerCase() === l.toLowerCase()) === i)
      .sort((a, b) => a.localeCompare(b, 'it'));
    const colors = { ...(this.state.studioUserLabelColors || {}) };
    if (colors[from] !== undefined) {
      colors[to] = colors[from];
      delete colors[from];
      this.state.studioUserLabelColors = colors;
    }
    for (const [key, t] of this.state.studioTargets.entries()) {
      const labels = t.labels || [];
      if (!labels.includes(from)) continue;
      this.state.studioTargets.set(key, {
        ...t,
        labels: replaceLabelInList(labels, from, to),
      });
    }
    if (this.state.studioDrafts instanceof Map) {
      for (const [key, d] of this.state.studioDrafts.entries()) {
        const labels = d.labels || [];
        if (!labels.includes(from)) continue;
        this.state.studioDrafts.set(key, mergeStudioDraft(d, { labels: replaceLabelInList(labels, from, to) }));
      }
      this._persistStudioDrafts();
    }
    if (this.state.studioLabelFilter === from) this.state.studioLabelFilter = to;
    refreshStudioAfterLabelCatalogChange(this);
    return res;
  },

  async deleteStudioUserLabel(name) {
    if (!this.state.userSession) return;
    const accessKey = this.state.userSession.getAccessCode();
    const res = await deleteUserLabel(accessKey, name);
    const normalized = res.name || normalizeLabelName(name);
    this.state.studioUserLabels = (this.state.studioUserLabels || []).filter((l) => l !== normalized);
    const colors = { ...(this.state.studioUserLabelColors || {}) };
    delete colors[normalized];
    this.state.studioUserLabelColors = colors;
    for (const [key, t] of this.state.studioTargets.entries()) {
      const labels = t.labels || [];
      if (!labels.includes(normalized)) continue;
      this.state.studioTargets.set(key, {
        ...t,
        labels: removeLabelFromList(labels, normalized),
      });
    }
    if (this.state.studioDrafts instanceof Map) {
      for (const [key, d] of this.state.studioDrafts.entries()) {
        const labels = d.labels || [];
        if (!labels.includes(normalized)) continue;
        const nextLabels = removeLabelFromList(labels, normalized);
        const merged = mergeStudioDraft(d, { labels: nextLabels });
        if (isStudioDraftEmpty(merged)) this.state.studioDrafts.delete(key);
        else this.state.studioDrafts.set(key, merged);
      }
      this._persistStudioDrafts();
    }
    if (this.state.studioLabelFilter === normalized) this.state.studioLabelFilter = null;
    refreshStudioAfterLabelCatalogChange(this);
    return res;
  },

  async loadStudioBudget() {
    if (!this.state.userSession) return;
    try {
      const accessKey = this.state.userSession.getAccessCode();
      this.state.studioBudget = await fetchStudioBudget(accessKey);
    } catch (err) {
      console.error('[Studio] Errore caricamento budget:', err);
      this.state.studioBudget = 500;
    }
  },

  async setStudioBudget(value) {
    const budget = Number(value) || 0;
    this.state.studioBudget = budget;
    this.renderStudioView();
    this.renderListaKpiStrip?.();
    try {
      const accessKey = this.state.userSession.getAccessCode();
      await saveStudioBudget(accessKey, budget);
    } catch (err) {
      console.error('[Studio] Errore salvataggio budget:', err);
    }
  },

  async addToStudio(playerKey, { maxPrice, notes, band, labels }) {
    const accessKey = this.state.userSession.getAccessCode();
    const current = this.state.studioTargets.get(playerKey) || {};
    const nextBand = normalizeBand(band || current.band);
    let sortOrder = Number.isFinite(Number(current.sortOrder)) ? Number(current.sortOrder) : null;
    if (sortOrder === null || (band !== undefined && nextBand !== normalizeBand(current.band))) {
      sortOrder = this._nextSortOrderForBand(nextBand, playerKey);
    }
    const record = {
      maxPrice,
      notes,
      sold: current.sold || false,
      band: nextBand,
      labels: labels || current.labels || [],
      backups: current.backups || [],
      missing: current.missing || false,
      sortOrder,
    };
    await upsertStudioTarget({ accessKey, playerKey, ...record });
    this.state.studioTargets.set(playerKey, record);
    this.clearStudioDraft(playerKey);
    this.updateStudioTabCount();
    this.renderStudioView();
    this.syncStudioButtonsInTable();
    this.syncListaStudioFilterContext?.();
    return record;
  },

  _nextSortOrderForBand(band, excludeKey = null) {
    let max = -1;
    for (const [key, t] of this.state.studioTargets.entries()) {
      if (excludeKey && key === excludeKey) continue;
      if (normalizeBand(t.band) !== band) continue;
      const n = Number(t.sortOrder);
      if (Number.isFinite(n) && n > max) max = n;
    }
    return max + 1;
  },

  _studioTargetPayload(playerKey, updated) {
    return {
      accessKey: this.state.userSession.getAccessCode(),
      playerKey,
      maxPrice: updated.maxPrice,
      notes: updated.notes,
      sold: updated.sold,
      band: updated.band,
      labels: updated.labels || [],
      backups: updated.backups || [],
      missing: updated.missing || false,
      sortOrder: Number.isFinite(Number(updated.sortOrder)) ? Number(updated.sortOrder) : 0,
    };
  },

  /** Patch campi target; soft=true evita re-render lista (ok se band non cambia). */
  async setStudioTargetFields(playerKey, fields = {}, { soft = false } = {}) {
    if (!this.state.studioTargets.has(playerKey)) return;
    const current = this.state.studioTargets.get(playerKey);
    const updated = { ...current, ...fields };
    if (fields.band !== undefined) {
      updated.band = normalizeBand(fields.band);
      if (normalizeBand(fields.band) !== normalizeBand(current.band) && fields.sortOrder === undefined) {
        updated.sortOrder = this._nextSortOrderForBand(updated.band, playerKey);
      }
    } else {
      updated.band = normalizeBand(updated.band);
    }
    if (!Number.isFinite(Number(updated.sortOrder))) {
      updated.sortOrder = this._nextSortOrderForBand(updated.band, playerKey);
    }
    delete updated.priority;
    await upsertStudioTarget(this._studioTargetPayload(playerKey, updated));
    this.state.studioTargets.set(playerKey, updated);
    const bandChanged =
      fields.band !== undefined &&
      normalizeBand(fields.band) !== normalizeBand(current.band);
    if (soft && !bandChanged) {
      this._syncStudioCardQuickFields?.(playerKey, updated);
      if (fields.labels !== undefined) {
        this._syncStudioRowTagsUi?.(playerKey, updated.labels || []);
        this.renderStudioToolbar?.();
      }
      this.syncStudioButtonsInTable();
      this.renderListaKpiStrip?.();
      return updated;
    }
    this.renderStudioView();
    this.syncStudioButtonsInTable();
    return updated;
  },

  async updateStudioTarget(playerKey, opts) {
    return this.setStudioTargetFields(playerKey, opts, { soft: false });
  },

  /** Toggle etichetta su un target senza ri-render completo della lista (popover resta aperto). */
  async toggleStudioTargetLabel(playerKey, labelName) {
    const name = normalizeLabelName(labelName);
    if (!name || !this.state.studioTargets.has(playerKey)) return;
    const current = this.state.studioTargets.get(playerKey);
    const labels = Array.isArray(current.labels) ? [...current.labels] : [];
    const idx = labels.indexOf(name);
    if (idx >= 0) labels.splice(idx, 1);
    else {
      labels.push(name);
      await this.ensureStudioUserLabelInCatalog(name);
    }
    return this.setStudioTargetFields(playerKey, { labels }, { soft: true });
  },

  /**
   * Riordina i target entro uno slot (stesso band). Persiste sort_order 0..n-1.
   * @param {string} band
   * @param {string[]} orderedKeys
   */
  async reorderStudioTargetsInBand(band, orderedKeys) {
    const slot = normalizeBand(band);
    if (!Array.isArray(orderedKeys) || orderedKeys.length === 0) return;
    const accessKey = this.state.userSession?.getAccessCode?.();
    if (!accessKey) return;

    const updates = [];
    orderedKeys.forEach((key, index) => {
      const current = this.state.studioTargets.get(key);
      if (!current) return;
      if (normalizeBand(current.band) !== slot) return;
      const updated = { ...current, sortOrder: index };
      this.state.studioTargets.set(key, updated);
      updates.push(upsertStudioTarget(this._studioTargetPayload(key, updated)));
    });
    try {
      await Promise.all(updates);
    } catch (err) {
      console.error('[Studio] Errore riordinamento:', err);
    }
    this.renderStudioView();
  },

  async removeFromStudio(playerKey) {
    const accessKey = this.state.userSession.getAccessCode();
    await deleteStudioTarget(accessKey, playerKey);
    this.state.studioTargets.delete(playerKey);
    this.updateStudioTabCount();
    this.renderStudioView();
    this.syncStudioButtonsInTable();
    this.syncListaStudioFilterContext?.();
  },

  async relinkStudioTarget(oldKey, newKey) {
    const current = this.state.studioTargets.get(oldKey);
    if (!current) return;
    const accessKey = this.state.userSession.getAccessCode();
    const moved = { ...current, missing: false, band: normalizeBand(current.band) };
    delete moved.priority;
    await upsertStudioTarget(this._studioTargetPayload(newKey, moved));
    await deleteStudioTarget(accessKey, oldKey);
    this.state.studioTargets.delete(oldKey);
    this.state.studioTargets.set(newKey, moved);
    this.updateStudioTabCount();
    this.renderStudioView();
    this.syncStudioButtonsInTable();
    this.syncListaStudioFilterContext?.();
  },

  async keepMissingTarget(playerKey) {
    await this.updateStudioTarget(playerKey, { missing: true });
  },

  async runStudioReconciliation() {
    if (this.state.userSession && typeof this.loadStudioTargets === 'function') {
      await this.loadStudioTargets();
    }
    if (!this.state.studioTargets || this.state.studioTargets.size === 0) {
      return { ok: [], recovered: [], changedTeam: [], missing: [] };
    }
    const targets = Array.from(this.state.studioTargets.entries()).map(
      ([playerKey, v]) => ({ playerKey, missing: !!(v && v.missing) }),
    );
    const result = reconcileStudioTargets(targets, this.state.allPlayers);

    for (const key of result.recovered) {
      await this.updateStudioTarget(key, { missing: false });
    }

    if (result.changedTeam.length || result.missing.length) {
      this.showStudioReconcileModal(result);
    }
    return result;
  },

  /**
   * Durante asta: «Preso da altri» — exit sull'asta attiva, non tocca Studio.
   */
  async markTakenByOthers(playerKey, playerInfo = null, anchorEl = null) {
    if (!playerKey) return;
    if (!this.state.activeAuctionId) {
      this.showUnifiedSetupScreen?.();
      return;
    }
    const info = playerInfo || parsePlayerKey(playerKey);
    this.showPresoForm?.(playerKey, info, anchorEl, { mode: 'others' });
  },

  async addStudioBackup(playerKey, name) {
    const clean = (name || '').trim();
    if (!clean) return;
    const current = this.state.studioTargets.get(playerKey) || {};
    const backups = Array.isArray(current.backups) ? [...current.backups] : [];
    if (backups.length >= 2) return;
    if (backups.some((b) => b.toLowerCase() === clean.toLowerCase())) return;
    backups.push(clean);
    await this.updateStudioTarget(playerKey, { backups });
  },

  async removeStudioBackup(playerKey, name) {
    const current = this.state.studioTargets.get(playerKey) || {};
    const backups = (current.backups || []).filter((b) => b !== name);
    await this.updateStudioTarget(playerKey, { backups });
  },

  updateStudioTabCount() {
    const el = document.getElementById('studio-count');
    if (el) el.textContent = this.state.studioTargets.size;
    this.syncAppShellCounts?.();
    this.renderListaKpiStrip?.();
  },

  switchToStudioTab(tab) {
    // Delega a switchAuctionScreen per retrocompatibilità
    this.switchAuctionScreen(tab === 'studio' ? 'studio' : 'lista');
  },

  _studioSegHtml(id, label, options, currentValue, { compact = false } = {}) {
    const buttons = options.map(([value, text]) => {
      const on = currentValue === value;
      return `<button type="button" class="studio-seg__btn${on ? ' is-on' : ''}" data-value="${value}" aria-pressed="${on ? 'true' : 'false'}">${text}</button>`;
    }).join('');
    return {
      labelHtml: `<span class="studio-form-label" id="${id}-label">${label}</span>`,
      controlHtml: `
        <div class="studio-seg${compact ? ' studio-seg--slots' : ''}" role="group" aria-labelledby="${id}-label" data-target="${id}">
          ${buttons}
        </div>
        <input type="hidden" id="${id}" value="${escapeHtml(currentValue)}">
      `,
    };
  },

  _createStudioInlineFormEl(playerKey, current = {}) {
    const band = normalizeBand(current.band);
    const form = document.createElement('div');
    form.id = 'studio-inline-form';
    form.className = 'studio-inline-form';
    form.dataset.playerKey = playerKey;
    form.setAttribute('role', 'form');
    form.setAttribute('aria-label', 'Target Studio');
    const slotSeg = this._studioSegHtml(
      'studio-input-band',
      'Slot',
      BANDS.map(({ key }) => [key, key]),
      band,
      { compact: true },
    );
    form.innerHTML = `
      <div class="studio-form-main">
        <div class="studio-form-head">
          <span class="studio-form-label">Prezzo max</span>
          ${slotSeg.labelHtml}
          <label class="studio-form-label" for="studio-input-notes">Note</label>
          <span class="studio-form-head-spacer" aria-hidden="true"></span>
        </div>
        <div class="studio-form-controls">
          <div class="studio-form-field studio-form-field--price">
            <input type="hidden" id="studio-input-price" value="${Number(current.maxPrice) > 0 ? Number(current.maxPrice) : ''}">
            <div class="studio-price-bands" id="studio-input-price-bands" role="listbox" aria-label="Soglia prezzo max">
              ${renderPriceBandPickerHtml(current.maxPrice || 0)}
            </div>
          </div>
          <div class="studio-form-field studio-form-field--seg studio-form-field--seg-compact">
            ${slotSeg.controlHtml}
          </div>
          <div class="studio-form-field studio-form-field--notes">
            <input type="text" class="studio-form-control studio-form-input studio-form-input--notes" id="studio-input-notes" value="${escapeHtml(current.notes || '')}" placeholder="Opzionale" autocomplete="off">
          </div>
          <div class="studio-form-actions">
            <button type="button" class="studio-form-cancel" id="studio-form-cancel">Annulla</button>
            <button type="button" class="studio-form-save" id="studio-form-save">Salva</button>
          </div>
        </div>
      </div>
      <div class="studio-form-labels" id="studio-form-labels">
        <span class="studio-form-label studio-form-label--inline">Etichette</span>
        <div class="studio-labels-row">
          <div class="studio-labels-chips" id="studio-labels-chips">
            ${(() => {
              const { custom } = this.getStudioLabelOptions(current.labels || []);
              const onSet = new Set(current.labels || []);
              return custom
                .map((l) => {
                  const on = onSet.has(l);
                  const chipColor = this.getStudioLabelColor?.(l) || DEFAULT_STUDIO_LABEL_COLOR;
                  return `<button type="button" class="tag-filter studio-label-chip studio-label-chip--custom${on ? ' active studio-label-chip--on' : ''}" data-label="${escapeHtml(l)}" aria-pressed="${on ? 'true' : 'false'}" data-label-color="${chipColor}" style="--chip-color: ${chipColor}">${escapeHtml(l)}</button>`;
                })
                .join('');
            })()}
          </div>
          <div class="studio-labels-add">
            <input type="text" class="studio-form-control" id="studio-input-newlabel" placeholder="Nuova…" maxlength="24" aria-label="Aggiungi etichetta" autocomplete="off">
            <button type="button" class="studio-labels-add-btn" id="studio-add-label" aria-label="Aggiungi etichetta">+</button>
          </div>
        </div>
      </div>
    `;
    return form;
  },

  _wireStudioInlineForm(form, playerKey) {
    const chipsEl = form.querySelector('#studio-labels-chips');
    const addCustomLabel = async () => {
      const input = form.querySelector('#studio-input-newlabel');
      const val = normalizeLabelName(input?.value || '');
      if (!val || !chipsEl) return;
      try {
        await this.createStudioUserLabel(val);
      } catch (err) {
        console.warn('[Studio] create label:', err);
      }
      const exists = Array.from(chipsEl.querySelectorAll('.studio-label-chip'))
        .some((c) => c.dataset.label.toLowerCase() === val.toLowerCase());
      if (!exists) {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'tag-filter studio-label-chip studio-label-chip--on studio-label-chip--custom active';
        chip.dataset.label = val;
        chip.setAttribute('aria-pressed', 'true');
        chip.textContent = val;
        applyLabelChipColor(chip, this.getStudioLabelColor(val));
        chipsEl.appendChild(chip);
      } else {
        const chip = Array.from(chipsEl.querySelectorAll('.studio-label-chip'))
          .find((c) => c.dataset.label.toLowerCase() === val.toLowerCase());
        if (chip) {
          chip.classList.add('studio-label-chip--on');
          chip.setAttribute('aria-pressed', 'true');
        }
      }
      input.value = '';
      input.focus();
    };
    const saveForm = async () => {
      const maxPrice = Number(form.querySelector('#studio-input-price')?.value) || 0;
      const band = normalizeBand(form.querySelector('#studio-input-band')?.value);
      const notes = form.querySelector('#studio-input-notes')?.value.trim() || '';
      const labels = Array.from(chipsEl?.querySelectorAll('.studio-label-chip--on') || [])
        .map((c) => c.dataset.label);
      await this.addToStudio(playerKey, { maxPrice, notes, band, labels });
      this.hideStudioInlineForm();
    };

    const priceBands = form.querySelector('#studio-input-price-bands');
    const priceHidden = form.querySelector('#studio-input-price');
    priceBands?.addEventListener('click', (e) => {
      const chip = e.target.closest('.studio-price-chip');
      if (!chip || !priceBands.contains(chip)) return;
      const next = Number(chip.dataset.priceMax) || 0;
      if (priceHidden) priceHidden.value = next > 0 ? String(next) : '';
      priceBands.querySelectorAll('.studio-price-chip').forEach((c) => {
        const on = c === chip;
        c.classList.toggle('is-on', on);
        c.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    });

    form.querySelectorAll('.studio-seg').forEach((seg) => {
      seg.addEventListener('click', (e) => {
        const btn = e.target.closest('.studio-seg__btn');
        if (!btn || !seg.contains(btn)) return;
        seg.querySelectorAll('.studio-seg__btn').forEach((b) => {
          const on = b === btn;
          b.classList.toggle('is-on', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        const hidden = form.querySelector(`#${seg.dataset.target}`);
        if (hidden) hidden.value = btn.dataset.value || '';
      });
    });

    chipsEl?.addEventListener('click', (e) => {
      const chip = e.target.closest('.studio-label-chip');
      if (!chip) return;
      const on = chip.classList.toggle('studio-label-chip--on');
      chip.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    form.querySelector('#studio-add-label')?.addEventListener('click', () => { void addCustomLabel(); });
    form.querySelector('#studio-input-newlabel')?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        void addCustomLabel();
      }
    });
    form.querySelector('#studio-form-save')?.addEventListener('click', () => { void saveForm(); });
    form.querySelector('#studio-form-cancel')?.addEventListener('click', () => this.hideStudioInlineForm());
    form.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.hideStudioInlineForm();
        return;
      }
      if (e.key === 'Enter' && e.target?.id !== 'studio-input-newlabel') {
        e.preventDefault();
        void saveForm();
      }
    });
  },

  _tableColCount(tableEl) {
    const headCount = tableEl?.querySelectorAll('thead th')?.length;
    if (headCount) return headCount;
    const firstRow = tableEl?.querySelector('tbody tr:not(.studio-inline-form-row)');
    return firstRow?.cells?.length || 12;
  },

  showStudioInlineForm(playerKey, anchorEl) {
    if (!anchorEl) return;
    this.hideStudioInlineForm();
    const current = this.state.studioTargets.get(playerKey) || {};
    const form = this._createStudioInlineFormEl(playerKey, current);
    this._wireStudioInlineForm(form, playerKey);

    const tableRow = anchorEl.tagName === 'TR' ? anchorEl : anchorEl.closest('tr');
    const playerCard = anchorEl.closest?.('.player-card');

    if (tableRow && tableRow.closest('tbody')) {
      const table = tableRow.closest('table');
      const formRow = document.createElement('tr');
      formRow.id = 'studio-inline-form-row';
      formRow.className = 'studio-inline-form-row';
      formRow.dataset.playerKey = playerKey;
      const cell = document.createElement('td');
      cell.colSpan = this._tableColCount(table);
      cell.className = 'studio-inline-form-cell';
      cell.appendChild(form);
      formRow.appendChild(cell);
      tableRow.insertAdjacentElement('afterend', formRow);
      formRow.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      const scrollHost = tableRow.closest('.players-table-container');
      if (scrollHost) scrollHost.scrollLeft = 0;
      form.querySelector('#studio-input-notes')?.focus();
      return;
    }

    if (playerCard) {
      form.classList.add('studio-inline-form--card');
      playerCard.insertAdjacentElement('afterend', form);
      form.querySelector('#studio-input-notes')?.focus();
      return;
    }

    form.classList.add('studio-inline-form--block');
    anchorEl.insertAdjacentElement('afterend', form);
    form.querySelector('#studio-input-notes')?.focus();
  },

  hideStudioInlineForm() {
    document.getElementById('studio-inline-form-row')?.remove();
    document.getElementById('studio-inline-form')?.remove();
  },

  toggleStudioLabelFilter(label) {
    this.state.studioLabelFilter = this.state.studioLabelFilter === label ? null : label;
    this.renderStudioView();
  },

  setStudioRoleFilter(role) {
    const next = role ? String(role).toUpperCase() : null;
    this.state.studioRoleFilter = this.state.studioRoleFilter === next ? null : next;
    this.renderStudioView();
  },

  setStudioTeamFilter(team) {
    const next = team ? String(team).trim() : null;
    this.state.studioTeamFilter = this.state.studioTeamFilter === next ? null : next;
    this.renderStudioView();
  },

  setStudioBandFilter(band) {
    let next = null;
    if (band === UNASSIGNED_STUDIO_SLOT_KEY) next = UNASSIGNED_STUDIO_SLOT_KEY;
    else if (band) next = normalizeBand(band);
    this.state.studioBandFilter = this.state.studioBandFilter === next ? null : next;
    this.renderStudioView();
  },

  clearStudioFilters() {
    this.state.studioLabelFilter = null;
    this.state.studioRoleFilter = null;
    this.state.studioTeamFilter = null;
    this.state.studioBandFilter = null;
    this.renderStudioView();
  },

  syncStudioButtonsInTable() {
    this._unpinAllStudioPopoverPanels?.();
    const auctionMode = this.state.auctionMode === 'live' ? 'live' : 'prep';
    document.body?.classList.toggle('auction-mode-live', auctionMode === 'live');
    document.body?.classList.toggle('auction-mode-prep', auctionMode === 'prep');

    const resolveKey = (el) => {
      let key = el.dataset.playerKey || '';
      let name = el.dataset.playerName || '';
      let team = el.dataset.playerTeam || '';
      if (!key) {
        const nested = el.querySelector('[data-player-key]');
        if (!nested) return null;
        key = nested.dataset.playerKey;
        name = nested.dataset.playerName || '';
        team = nested.dataset.playerTeam || '';
      }
      return { key, name, team };
    };

    const liveAstaHtmlFor = (key, name, team) => {
      const inRoster = this.state.roster instanceof Map && this.state.roster.has(key);
      const exits = this.state.auctionExits instanceof Map ? this.state.auctionExits : null;
      const exited = exits?.has(key) || false;
      const exit = exited ? exits.get(key) : null;
      const exitPrice = Number(exit?.price);
      const exitHasValidPrice = Number.isFinite(exitPrice) && exitPrice >= 1;
      const rosterEntry = inRoster ? this.state.roster.get(key) : null;
      const rosterPrice = Number(rosterEntry?.price);
      const assignedExitMode = this.state.rosaExitMode === 'assigned';
      let exitOpponentLabel = '';
      if (assignedExitMode && exit?.opponentId) {
        const opps = Array.isArray(this.state.auctionOpponents) ? this.state.auctionOpponents : [];
        const idx = opps.findIndex((o) => String(o.id) === String(exit.opponentId));
        const found = idx >= 0 ? opps[idx] : null;
        exitOpponentLabel = (found?.name && String(found.name).trim())
          || (idx >= 0 ? `Avversario ${idx + 1}` : '·');
      } else if (assignedExitMode && exited) {
        exitOpponentLabel = 'Da assegnare';
      }
      return renderLiveAstaActions({
        playerKey: key,
        name,
        team,
        inRoster,
        exited,
        exitHasValidPrice,
        exitPrice: exitHasValidPrice ? exitPrice : 0,
        exitOpponentLabel,
        assignedExitMode,
        rosterPrice: Number.isFinite(rosterPrice) ? rosterPrice : 0,
      });
    };

    document.querySelectorAll('.action-buttons-container, .pc-actions').forEach((container) => {
      const ids = resolveKey(container);
      if (!ids) return;
      const { key, name, team } = ids;
      container.dataset.playerKey = key;
      container.dataset.playerName = name;
      container.dataset.playerTeam = team;

      const children = [];

      if (auctionMode === 'live') {
        const presoSlot = document.createElement('div');
        presoSlot.className = 'preso-action-slot pc-asta';
        presoSlot.dataset.playerKey = key;
        presoSlot.dataset.playerName = name;
        presoSlot.dataset.playerTeam = team;
        presoSlot.innerHTML = liveAstaHtmlFor(key, name, team);
        children.push(presoSlot);
      }

      const inStudio = this.state.studioTargets.has(key);
      const target = this.getStudioListSeed?.(key) || (inStudio
        ? this.state.studioTargets.get(key)
        : { maxPrice: 0, band: null, labels: [], notes: '' });

      const wrap = document.createElement('div');
      wrap.className = 'studio-list-actions studio-list-actions--quick';
      if (auctionMode === 'live') {
        wrap.classList.add('studio-list-actions--quick-live');
      }
      wrap.dataset.playerKey = key;
      wrap.dataset.playerName = name;
      wrap.dataset.playerTeam = team;
      wrap.setAttribute('data-testid', 'studio-list-quick');
      this._appendStudioQuickActions?.(wrap, key, target, {
        name,
        idScope: 'list',
        readonly: auctionMode === 'live',
      });
      children.push(wrap);

      container.replaceChildren(...children);
    });

    filtersManager.setListContext({
      studioTargets: this.state.studioTargets,
      studioDrafts: this.state.studioDrafts,
    });
  },

  _refreshStudioCompareUI() {
    this.renderStudioCompare?.();
    this._syncCompareToggleStates?.();
  },

  addPlayerToStudioCompare(playerKey) {
    if (!playerKey) return;
    let slots = this.state.studioCompareSlots;
    if (!Array.isArray(slots) || slots.length !== STUDIO_COMPARE_SLOT_COUNT) {
      slots = createEmptyCompareSlots();
      this.state.studioCompareSlots = slots;
    }

    const opening = !this.state.studioCompareModalOpen;
    if (opening) {
      this.state.studioCompareModalOpen = true;
      this.state.studioCompareSlots = createEmptyCompareSlots();
      this.state.studioCompareSlots[0] = playerKey;
      this._refreshStudioCompareUI();
      return;
    }

    const existingIdx = slots.indexOf(playerKey);
    if (existingIdx >= 0) return;

    const emptyIdx = slots.findIndex((s) => !s);
    if (emptyIdx < 0) {
      window.showToast?.('Puoi confrontare al massimo 5 giocatori', 'info');
      return;
    }
    slots[emptyIdx] = playerKey;
    this._refreshStudioCompareUI();
  },

  openStudioCompare() {
    this.state.studioCompareModalOpen = true;
    if (
      !Array.isArray(this.state.studioCompareSlots)
      || this.state.studioCompareSlots.length !== STUDIO_COMPARE_SLOT_COUNT
    ) {
      this.state.studioCompareSlots = createEmptyCompareSlots();
    }
    this._refreshStudioCompareUI();
  },

  closeStudioCompare() {
    this.state.studioCompareModalOpen = false;
    this.state.studioCompareSlots = createEmptyCompareSlots();
    this._refreshStudioCompareUI();
  },

  setStudioCompareSlot(slotIndex, playerKey) {
    const slots = this.state.studioCompareSlots;
    if (!Array.isArray(slots) || slotIndex < 0 || slotIndex >= STUDIO_COMPARE_SLOT_COUNT) return;
    const key = playerKey || null;
    if (key) {
      const dup = slots.indexOf(key);
      if (dup >= 0 && dup !== slotIndex) slots[dup] = null;
    }
    slots[slotIndex] = key;
    this._refreshStudioCompareUI();
  },
};
